// mainMTDPage.tsx
import { useEffect, useMemo, useState } from 'react';
import { useApiData,  } from '../../contexts/apiService';
import {MainTableItem, BottomsMainItem, NglSchedule} from '../../contexts/tableDefinitions';
import { ErrorBoundary } from "react-error-boundary";
import './mainMTDPage.css';
import './components/MTDcomponents.css';
import { getHoustonDateInfo } from '../../helper_functions';
import LoaderWheel from '../../components/common/LoaderWheel';
import SummaryList from './components/list_summary/listSummary';
import MainTable from './components/mainTable';
import TimeframeButtons from './components/timeframeButtons';
import StatusButton from './components/filter_buttons/filterButtons';
import Dropdown, { DropdownOption } from '../../components/common/dropdown/dropdown';
import FilterButtons from './components/filter_buttons/filterButtons';
import { useUserAccess } from '../../contexts/userAccess';
import {Chart} from 'react-google-charts';
import { useDataManager } from "../../contexts/dataManager";
import { title } from 'process';

const MainMTDPage = () => {
	const { apiData, isLoading } = useApiData();
    const { requestTableData, hasTableData } = useDataManager();
    const { accessToDeals } = useUserAccess();
    const [activeTab, setActiveTab] = useState<'chart' | 'table'>('chart');
    const [selectedMainView, setSelectedMainView] = useState<string>('producer');
    const [selectedView, setSelectedView] = useState<string>('monthly');
    const [selectedProducers, setSelectedProducers] = useState<string[]>([]);
    const [selectedDestinations, setSelectedDestinations] = useState<string[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<string>('all');
    const [selectedProduct, setSelectedProduct] = useState<string>('crude_oil');
    const [selectedPersonalFilter, setSelectedPersonalFilter] = useState<string>('personal_deals');

    const [selectedMonthYear, setSelectedMonthYear] = useState<string>('');
    const [alreadyFetched, setAlreadyFetched] = useState<Set<string>>(new Set());

    const houstonDateInfo = useMemo(() => getHoustonDateInfo(), []);
   
    const handleEmptyMonth = (monthYear: string) => {
        // Use requestTableData which handles duplicate request prevention
        if (!hasTableData('crude_main', monthYear)) {
            requestTableData('crude_main', monthYear);
        }
    };
    
    // Generate month/year options from the date range
    const monthYearOptions = useMemo((): DropdownOption[] => {
        if (!apiData?.date_ranges) {
            return [];
        }
        const { min_date, max_date } = apiData.date_ranges.crude_main;
        
        // Add null checks
        if (!min_date || !max_date) return [];
        
        const startDate = new Date(min_date);
        const endDate = new Date(max_date);
        
        // Validate dates are valid
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return [];
        
        const options: DropdownOption[] = [];
        const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
        
        while (current <= endDate) {
            const year = current.getFullYear();
            const month = current.getMonth();
            const monthName = current.toLocaleString('default', { month: 'long' });
            
            const value = `${year}-${String(month + 1).padStart(2, '0')}`; // Format: "2024-07"
            const label = `${monthName} ${year}`; // Format: "July 2024"
            
            options.push({ value, label });
            
            // Move to next month
            current.setMonth(current.getMonth() + 1);
        }
        
        return options.reverse(); // Most recent first
    }, [apiData?.date_ranges]);
    

    // Set default month/year to the latest available option
    useEffect(() => {
        if (monthYearOptions.length > 0 && !selectedMonthYear) {
            setSelectedMonthYear(monthYearOptions[0].value);
        }
    }, [monthYearOptions, selectedMonthYear]);

    // Handle month/year selection changes
    const handleMonthYearChange = (monthYear: string) => {
        setSelectedMonthYear(monthYear);
    };
    useEffect(() => {
        // Request data for tables
        if (selectedMainView === 'producer') {
            if (!hasTableData('crude_main', selectedMonthYear)) {
                requestTableData('crude_main', selectedMonthYear);
            }
            if (!hasTableData('date_ranges')) {
                requestTableData('date_ranges');
            }
            // if (!hasTableData('crude_main', selectedMonthYear)) {
            //     requestTableData('crude_main', selectedMonthYear);
            // }
            // if (!hasTableData('crude_main', selectedMonthYear)) {
            //     requestTableData('crude_main', selectedMonthYear);
            // }
            
            // if (!hasTableData('date_ranges')) {
            //     requestTableData('date_ranges');
            // }
        }
    }, [selectedMainView, selectedMonthYear]);
    const isCurrentMonth = (monthYear: string): boolean => {
        if (!monthYear) return true; // If no month selected, consider it current
        
        const [year, month] = monthYear.split('-');
        const selectedYear = parseInt(year);
        const selectedMonth = parseInt(month);
        
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;
        
        return selectedYear === currentYear && selectedMonth === currentMonth;
    };

    type FilterableItem =  MainTableItem | BottomsMainItem | NglSchedule;

    const filterDataByMonthYear = <T extends FilterableItem>(
        data: T[],
        selectedMonthYear: string,
        int_values: boolean,
    ): T[] => {
        if (!selectedMonthYear) {
            return data; // No month/year filter selected, return all data
        }
        
        const [year, month] = selectedMonthYear.split('-');
        const selectedYear = parseInt(year);
        const selectedMonthName = new Date(selectedYear, parseInt(month) - 1, 1)
            .toLocaleString('default', { month: 'long' });

        if (!int_values) {
            return data.filter(item => {
                return item.year == String(selectedYear) && item.month == selectedMonthName;
            });
        } else {
            return data.filter(item => {
                return item.year == selectedYear && item.month == parseInt(month);
            });
        }
    };

    const monthYearFilteredData = useMemo(() => {
        
        if(selectedMainView === 'producer'){
            if (apiData?.crude_main) {
                const filteredByMonth = filterDataByMonthYear(apiData.crude_main, selectedMonthYear, false);
                // Filter by producer access if accessToDeals has values
                if (selectedPersonalFilter === 'personal_deals' && accessToDeals.length > 0) {
                    return filteredByMonth.filter(item => 
                        item.producer && accessToDeals.includes(item.producer)
                    );
                }
                
                // If accessToDeals is empty, return all data (access to all)
                return filteredByMonth;
            }
        }
        if(selectedMainView === 'destination'){
            
        
        }
        return [];

    }, [apiData?.crude_main, selectedMainView, selectedMonthYear, accessToDeals, selectedPersonalFilter]);

    const monthYearFilteredBottomsData = useMemo(() => {
        if (apiData?.bottoms_main_table) {
            const producerMultipliers: Record<string, number> = {
                'Chevron': 0.198,
                'Phillips 66': 0.40,
                'Howard': 0.375,
                'Matador': 0.25,
                'Aris Water': 0.375,
                'Northwind Midstream': 0.25,
                'Wink Amine Treater': 0.25
            };
    
            let wtiCmatValue = 60; // Default fallback
            if (apiData?.components_pricing) {
                const monthToNumber = {
                    'January': 1, 'February': 2, 'March': 3, 'April': 4,
                    'May': 5, 'June': 6, 'July': 7, 'August': 8,
                    'September': 9, 'October': 10, 'November': 11, 'December': 12
                };
                
                let matchingRecord = null;
                
                // If target month/year are provided, look for exact match first
                if (selectedMonthYear) {
                    const [year, month] = selectedMonthYear.split('-');
                    matchingRecord = apiData.components_pricing.find((record: any) => 
                        record.cmat && 
                        record.month === month && 
                        Number(record.year) === Number(year)
                    );
                }
    
                // If no exact match found or no target specified, fall back to latest available
                if (!matchingRecord) {
                    const sortedRecords = apiData.components_pricing
                        .filter((record: any) => record.cmat)
                        .sort((a: any, b: any) => {
                            const yearA = Number(a.year) || 0;
                            const yearB = Number(b.year) || 0;
                            
                            if (yearB !== yearA) return yearB - yearA;
                            
                            const monthA = monthToNumber[a.month as keyof typeof monthToNumber] || 0;
                            const monthB = monthToNumber[b.month as keyof typeof monthToNumber] || 0;
                            
                            return monthB - monthA;
                        });
                    
                    matchingRecord = sortedRecords[0];
                }
                
                if (matchingRecord && matchingRecord.cmat) {
                    wtiCmatValue = Number(matchingRecord.cmat) || 60;
                }
            }
    
            // Add id field and calculate net_profit
            const dataWithCalculations = apiData.bottoms_main_table.map(row => {
                const netVolume = Number(row.Net_Barrels) || 0;
                const operator = row.Operator || '';
                const multiplier = producerMultipliers[operator] || 0.25;
                const net_profit = netVolume * multiplier * (wtiCmatValue - 6.19);
    
                return {
                    ...row,
                    id: row.ID,
                    net_profit: net_profit
                };
            });
    
            return filterDataByMonthYear(dataWithCalculations, selectedMonthYear, true);
        }
    }, [apiData?.bottoms_main_table, apiData?.components_pricing, selectedMonthYear]);

    const monthYearFilteredNGLData = useMemo(() => {
        if (apiData?.ngl_schedule_table) {
            // add month and year columns based on loadDate
            const dataWithMonthYear = apiData.ngl_schedule_table.map(row => ({
                ...row,
                month: new Date(row.loadDate).getMonth() + 1, // +1 because getMonth() returns 0-11
                year: new Date(row.loadDate).getFullYear()
            }));

            const dataWithQuantity = dataWithMonthYear.map(row => ({
                ...row,
                quantity: 7500
            }));

            const destinationMargins: Record<string, number> = {
                'Sherhan': 0.0951,
                'James Lake': 0.0302,
            };

            const dataWithCalculations = dataWithQuantity.map(row => {
                const netVolume = Number(row.quantity) || 7500;
                const destination = row.marketDestination || '';
                const multiplier = destinationMargins[destination] || 0;
                const net_profit = multiplier * netVolume;
    
                return {
                    ...row,
                    net_profit: net_profit
                };
            });

            return filterDataByMonthYear(dataWithCalculations, selectedMonthYear, true);
        }
    }, [apiData?.ngl_schedule_table, selectedMonthYear]);

    useEffect(() => {
        if (monthYearFilteredData && monthYearFilteredData.length === 0 && selectedMonthYear) {
            handleEmptyMonth(selectedMonthYear);
        }
    }, [monthYearFilteredData?.length, selectedMonthYear]);

    const filterDataByTimePeriod = <T extends FilterableItem>(
        data: T[],
        date_col_name: string,
        selectedMonthYear: string,
        selectedView: string,
        int_values: boolean,
    ): T[] => {
        
        if (!data || data.length === 0) {
            return [];
        }
        
        // If a specific month/year is selected and it's not the current month, skip the time-based filters
        if (selectedMonthYear && !isCurrentMonth(selectedMonthYear)) {
            return data;
        }
        
        const filterFunctions = {
            daily: (item: T) => {
                const dateValue = item[date_col_name as keyof T];
                if (!dateValue) return false;
                const loadDateString = new Date(dateValue as string | Date).toISOString().split('T')[0];
                return loadDateString === houstonDateInfo.todayString || 
                       loadDateString === houstonDateInfo.yesterdayString;
            },
            
            weekly: (item: T) => {
                const dateValue = item[date_col_name as keyof T];
                if (!dateValue) return false;
                const loadDate = new Date(dateValue as string | Date);
                const loadDateString = loadDate.toISOString().split('T')[0];
                const today = new Date(houstonDateInfo.todayString);
                const sevenDaysAgo = new Date(today);
                sevenDaysAgo.setDate(today.getDate() - 6); // 6 days ago + today = 7 days total
                const sevenDaysAgoString = sevenDaysAgo.toISOString().split('T')[0];
                
                return loadDateString >= sevenDaysAgoString && loadDateString <= houstonDateInfo.todayString;
            },
            
            monthly: (item: T) => {
                // Filter based on month and year columns, not date column
                if (!int_values) {
                    return item.month === houstonDateInfo.currentMonthName && 
                    item.year === houstonDateInfo.currentYear;
                } else {
                    return item.month === houstonDateInfo.currentMonthNumber && 
                    item.year === houstonDateInfo.currentYearNumber;
                }
            }
        };
    
        const filterFunction = filterFunctions[selectedView as keyof typeof filterFunctions];
        if (filterFunction) {
            return data.filter(filterFunction);
        }

        return data
    };

    const dateFilteredData = useMemo(() => {
        if (monthYearFilteredData) {
            return filterDataByTimePeriod(monthYearFilteredData, 'load_date', selectedMonthYear, selectedView, false);
        }
    }, [monthYearFilteredData, selectedMonthYear, selectedView, houstonDateInfo]);
    
    const dateFilteredBottomsData = useMemo(() => {
        if (monthYearFilteredBottomsData) {
            return filterDataByTimePeriod(monthYearFilteredBottomsData, 'final_load_date', selectedMonthYear, selectedView, true);
        }
    }, [monthYearFilteredBottomsData, selectedMonthYear, selectedView, houstonDateInfo]);

    const dateFilteredNGLData = useMemo(() => {
        if (monthYearFilteredNGLData) {
            return filterDataByTimePeriod(monthYearFilteredNGLData, 'loadDate', selectedMonthYear, selectedView, true);
        }
    }, [monthYearFilteredNGLData, selectedMonthYear, selectedView, houstonDateInfo]);
    
    // Status filter function (completed/rejected)
    const statusFilter = (item: MainTableItem) => {
        if (selectedStatus === 'all') return true;

        const status = item.status;
        if (!status) return false;
        
        const statusLower = status.toString().toLowerCase();
        
        if (selectedStatus === 'completed') {
            return statusLower === 'completed';
        } else if (selectedStatus === 'rejected') {
            return statusLower === 'rejected';
        }
        
        return true;
    };

    // Apply status filtering to data
    const statusFilteredData = useMemo(() => {
        if (!dateFilteredData) return [];
        
        return dateFilteredData.filter(statusFilter);
    }, [dateFilteredData, selectedStatus]);

    // Filter main_table data based on selected row (producer or destination/market)
    const finalTableData = useMemo(() => {

        if (selectedProduct === "tank_bottoms") {
            if (!dateFilteredBottomsData) return [];
            // If no producers are selected, return all date-filtered data
            if (selectedProducers.length === 0) {
                return dateFilteredBottomsData;
            }
            
            // Filter by selected producers
            return dateFilteredBottomsData.filter(item => 
                item.Operator && selectedProducers.includes(item.Operator)
            );

        } else if (selectedProduct === "ngl") {
            if (!dateFilteredNGLData) return [];

            // If no producers are selected, return all date-filtered data
            if (selectedProducers.length === 0) {
                return dateFilteredNGLData;
            }
            
            // Filter by selected producers
            return dateFilteredNGLData.filter(item => 
                item.customer && selectedProducers.includes(item.customer)
            );
        }

        if (!statusFilteredData) return [];
        
        if (selectedMainView === 'producer') {
            // If no producers are selected, return all date-filtered data
            if (selectedProducers.length === 0) {
                return statusFilteredData;
            }
            
            // Filter by selected producers
            return statusFilteredData.filter(item => 
                item.producer && selectedProducers.includes(item.producer)
            );
        } else if (selectedMainView === 'destination') {
            // If no destinations are selected, return all date-filtered data
            if (selectedDestinations.length === 0) {
                return statusFilteredData;
            }
            
            // Filter by selected destinations
            return statusFilteredData.filter(item => 
                item.destination && selectedDestinations.includes(item.destination)
            );
        } else {
            // If no destinations or markets are selected, return all date-filtered data
            if (selectedDestinations.length === 0) {
                return statusFilteredData;
            }
            
            // Filter by selected markets
            return statusFilteredData.filter(item => 
                item.market && selectedDestinations.includes(item.market)
            );
        }

    }, [statusFilteredData, selectedProducers, selectedDestinations, selectedProduct]);
    
    const [yAxis, setYAxis] = useState<'gross_profit' | 'gross_barrels' | 'margin'|'net_barrels'>('gross_profit');
    const [dataSeries, setDataSeries] = useState<'bsw' | 'gross_barrels' | 'gravity'>('bsw');
    const [selectedChartType, setSelectedChartType] = useState<'summary_chart' | 'projection_chart'|'histogram_chart'>('summary_chart');
    function convertYAxisName(name:string) :string{
        switch(name){
            case 'gross_profit':
                return "Gross Profit"
            case 'gross_barrels':
                return "Gross Barrels"
            case 'margin':
                return "Margin"
            case 'net_barrels':
                return "Net Barrels"
        }
            
        return "Unknown"
    }

    /**
     * Renders a bar chart displaying aggregated data by producer/customer for the MTD (Month-To-Date) status.
     *
     * The chart visualizes different metrics (Gross Profit, Gross Barrels, Net Barrels, or Margin) based on the selected y-axis.
     * Data is aggregated and sorted accordingly, and the chart updates when `finalTableData` or `yAxis` changes.
     *
     * @component
     * @returns {JSX.Element} The rendered bar chart component.
     *
     * @remarks
     * - Uses Google Charts via the `Chart` component.
     * - Handles chart events such as `select`, `ready`, and `error`.
     * - Dynamically sets chart data and axis titles based on props and state.
     *
     * @example
     * ```tsx
     * <BarChartData />
     * ```
     *
     * @dependencies
     * - React hooks: `useState`, `useEffect`, `useMemo`
     * - External: `Chart` component (Google Charts wrapper)
     * - Utility: `convertYAxisName` for axis title formatting
     */
    function BarChartData() {
        const [chartData, setChartData] = useState<(string | number|null)[][]>([["Producer", "Gross Profit"]]);
        const chartEvents = [
            {
                eventName: "select" as const,
                callback({ chartWrapper }: { chartWrapper: any }) {
                                     
                },
            },
            {
                eventName: "ready" as const,
                callback({ chartWrapper }: { chartWrapper: any }) {
                //console.log("Chart ready. ", chartWrapper.getChart());
                },
            },
            {
                eventName: "error" as const,
                callback(args: any) {
                //console.log("Chart errored. ", args);

                },
            },
        ];
        const chartOptions = useMemo(() => ({
            title: "MTD Status",
            hAxis: { title: "Producer" },
            legend: "none"
        }), []);

        

        useEffect(() => {
            if (!finalTableData?.length) {
                setChartData([["Producer", "Gross Profit"],[0,0]]);
                return;
            }

            // Aggregate by producer/customer
            const grouped = finalTableData.reduce<Record<string, { net_profit: number, gross_barrels: number, net_barrels:number }>>((acc, item) => {
                const producer =
                    (item as any).producer ??
                    (item as any).Operator ??
                    (item as any).customer ??
                    "unknown";
                const net_profit = Number(item.net_profit) || 0;
                const gross_barrels = Number((item as any).gross_barrels ?? (item as any).Gross_Barrels) || 0;
                const net_barrels = Number((item as any).net_barrels ?? (item as any).Net_Barrels) || 0;
                if (!acc[producer]) acc[producer] = { net_profit: 0, gross_barrels: 0, net_barrels: 0 };
                acc[producer].net_profit += net_profit;
                acc[producer].gross_barrels += gross_barrels;
                acc[producer].net_barrels = (acc[producer].net_barrels || 0) + net_barrels;
                return acc;
            }, {});

            let data: (string | number)[][];
            switch (yAxis) {
                case 'gross_barrels':
                    data = [
                        ["Producer", "Gross Barrels"],
                        ...Object.entries(grouped)
                            .sort((a, b) => b[1].gross_barrels - a[1].gross_barrels)
                            .map(([producer, vals]) => [producer, vals.gross_barrels])
                    ];
                    break;
                case 'net_barrels':
                    data = [

                        ["Producer", "Net Barrels"],

                        ...Object.entries(grouped)
                            .sort((a, b) => b[1].net_barrels - a[1].net_barrels)
                            .map(([producer, vals]) => [producer, vals.net_barrels])
                    ];
                    break;
                case 'margin':
                    data = [
                        ["Producer", "Margin"],
                        ...Object.entries(grouped)
                            .sort((a, b) => {
                                const marginA = a[1].net_barrels ? a[1].net_profit / a[1].net_barrels : 0;
                                const marginB = b[1].net_barrels ? b[1].net_profit / b[1].net_barrels : 0;
                                return marginB - marginA;
                            })
                            .map(([producer, vals]) => [
                                producer,
                                vals.net_barrels ? vals.net_profit / vals.net_barrels : 0
                            ])
                    ];
                    break;
                default:
                    data = [
                        ["Producer", "Gross Profit"],
                        ...Object.entries(grouped)
                            .sort((a, b) => b[1].net_profit - a[1].net_profit)
                            .map(([producer, vals]) => [producer, vals.net_profit])
                    ];
            }

            setChartData(data);
        }, [finalTableData, yAxis]);

        const vAxisTitle = convertYAxisName(yAxis);

        return (
            <div>
                <div>
                    <Chart
                        chartType='ColumnChart'
                        width='100%'
                        height='50em'
                        data={chartData}
                        chartEvents={chartEvents}
                        options={{
                            ...chartOptions,
                            vAxis: { title: vAxisTitle }
                        }}
                    />
                </div>
            </div>
        );
    }
    /**
     * Renders a line chart displaying monthly time series data (e.g., Gross Profit, Barrels, Margin)
     * with actual, cumulative, and projected cumulative values, depending on the selected month and year.
     * 
     * - For the current month, shows actuals up to today, cumulative sum, and a projected cumulative line using linear regression.
     * - For past months, shows actuals and cumulative only.
     * - Dynamically adapts chart axes, series, and titles based on the selected Y-axis metric.
     * - Handles empty data gracefully by displaying a default chart.
     * 
     * Dependencies:
     * - Uses React hooks (`useState`, `useEffect`, `useMemo`).
     * - Expects `finalTableData`, `yAxis`, and `selectedMonthYear` from parent scope.
     * - Utilizes Google Charts via the `Chart` component.
     * 
     * @returns {JSX.Element} A responsive line chart with interactive events and dynamic data.
     */
    function LineChartData() {
        const [chartData, setChartData] = useState<(string | number | null)[][]>([["Date", "Gross Profit"]]);
        const chartEvents = [
            {
                eventName: "select" as const,
                callback({ chartWrapper }: { chartWrapper: any }) {
                                     
                },
            },
            {
                eventName: "ready" as const,
                callback({ chartWrapper }: { chartWrapper: any }) {
                console.log("Chart ready. ", chartWrapper.getChart());
                },
            },
            {
                eventName: "error" as const,
                callback(args: any) {
                // Chart ready event
                },
            },
            {
                eventName: "error" as const,
                callback(args: any) {
                // Chart errored event

                },
            },
        ];
        const chartOptions = useMemo(() => ({
            title: "MTD Status",
            hAxis: { title: "Date" },
            legend: { position: 'bottom' },
            pointSize: 4,
            animation: {
                duration: 500,
                easing: 'out'
            }
        }), []);

        useEffect(() => {
            if (!finalTableData?.length) {
                setChartData([["Date", "Gross Profit", "Cumulative", "Projected Cumulative"], [0, 0, 0, 0]]);
                return;
            }


            // 1. Get year and month from selectedMonthYear
            const [yearStr, monthStr] = selectedMonthYear.split('-');
            const year = parseInt(yearStr, 10);
            const month = parseInt(monthStr, 10);

            // 2. Generate all dates in the month
            const daysInMonth = new Date(year, month, 0).getDate();
            const allDates: string[] = [];
            for (let d = 1; d <= daysInMonth; d++) {
                const date = new Date(year, month - 1, d);
                allDates.push(date.toISOString().split('T')[0]);
            }

            // 3. Aggregate by date
            const grouped = finalTableData.reduce<Record<string, { net_profit: number, gross_barrels: number, net_barrels: number }>>((acc, item) => {
                const dateValue = (item as any).load_date;
                const dateObj = dateValue ? new Date(dateValue) : null;
                const date = dateObj ? dateObj.toISOString().split('T')[0] : "unknown";
                const net_profit = Number(item.net_profit) || 0;
                const gross_barrels = Number((item as any).gross_barrels ?? (item as any).Gross_Barrels) || 0;
                const net_barrels = Number((item as any).net_barrels ?? (item as any).Net_Barrels) || 0;
                if (!acc[date]) acc[date] = { net_profit: 0, gross_barrels: 0, net_barrels: 0 };
                acc[date].net_profit += net_profit;
                acc[date].gross_barrels += gross_barrels;
                acc[date].net_barrels += net_barrels;
                return acc;
            }, {});

            // Helper to get y value for a date
            function getY(date: string) {
                switch (yAxis) {
                    case 'gross_barrels':
                        return grouped[date]?.gross_barrels ?? 0;
                    case 'net_barrels':
                        return grouped[date]?.net_barrels ?? 0;
                    case 'margin':
                        return grouped[date]?.net_barrels
                            ? grouped[date].net_profit / grouped[date].net_barrels
                            : 0;
                    default:
                        return grouped[date]?.net_profit ?? 0;
                }
            }


            let data: (string | number|null)[][];
            if (
                year === new Date().getFullYear() &&
                month === new Date().getMonth() + 1
            ) {
                // Current month: show actuals, cumulative, projected cumulative
                const today = new Date();
                const todayDay = today.getDate();
                const actualDates = allDates.slice(0, todayDay);

                // Build actuals and cumulative
                let actuals: [string, number|null][] = [];
                let cumulative: number[] = [];
                let cumSum = 0;
                for (let i = 0; i < allDates.length; i++) {
                    const date = allDates[i];
                    const y = i < actualDates.length ? getY(date) : null;
                    if (y !== null && y !== undefined) {
                        cumSum += Number(y);
                        cumulative.push(cumSum);
                    } else {
                        cumulative.push(i === 0 ? 0 : cumulative[i - 1]);
                    }
                    actuals.push([date, y]);
                }

                // Least squares regression on cumulative up to today
                let projectedCumulative: (number | null)[] = [];
                if (todayDay > 1) {
                    // Prepare x (day index) and y (cumulative) arrays
                    const xs = [] as any;
                    const ys = [] as any;
                    for (let i = 0; i < todayDay; i++) {
                        xs.push(i);
                        ys.push(cumulative[i]);
                    }
                    const n = xs.length;
                    const sumX = xs.reduce((a:any, b:any) => a + b, 0);
                    const sumY = ys.reduce((a:any, b:any) => a + b, 0);
                    const sumXY = xs.reduce((a:any, b:any, i:any) => a + b * ys[i], 0);
                    const sumXX = xs.reduce((a:any, b:any) => a + b * b, 0);

                    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
                    const intercept = (sumY - slope * sumX) / n;
                    // Project for all current/future days in month
                    for (let i = 0; i < allDates.length; i++) {
                        if (i < todayDay-1) {
                            projectedCumulative.push(null);
                        } else {
                            // Projected cumulative for day i
                            projectedCumulative.push(Math.round((slope * i + intercept) * 100) / 100);
                        }
                    }
                } else {
                    projectedCumulative = allDates.map(() => null);
                }

                // Merge for Google Charts: [date, actual, cumulative, projected cumulative]
                data = [
                    ["Date", "Actual", "Cumulative", "Projected Cumulative"],
                    ...allDates.map((date, i) => [
                        date,
                        actuals[i][1] ?? 0,
                        cumulative[i] ?? 0,
                        projectedCumulative[i]
                    ])
                ];
            } else {
                // Not current month: fill 0 for missing, cumulative
                let cumSum = 0;
                // ...inside LineChartData useEffect, in the "else" block for past months...

                data = [
                    ["Date", convertYAxisName(yAxis), "Cumulative"],
                    ...allDates.map(date => {
                        const y = getY(date);
                        cumSum += Number(y);
                        return [date, y, cumSum];
                    })
                ];
            }

            setChartData(data);
        }, [finalTableData, yAxis, selectedMonthYear]);

        const vAxisTitle =
            yAxis === 'gross_profit'
                ? 'Gross Profit'
                : yAxis === 'gross_barrels'
                ? 'Gross Barrels'
                : yAxis === 'net_barrels'
                ? 'Net Barrels'
                : 'Margin (Gross Profit / Gross Barrels)';

        return (
            <div>
                <div>
                    <Chart
                        chartType='LineChart'
                        width='100%'
                        height='50em'
                        data={chartData}
                        chartEvents={chartEvents}
                        options={{
                            ...chartOptions,
                            vAxis: { title: vAxisTitle },
                            vAxes: [
                                { title: vAxisTitle, minValue: 0 }, // Primary axis (left)
                                { title: vAxisTitle+' Cumulative', minValue: 0,  } // Secondary axis (right)
                            ],
                            series: chartData[0]?.length === 4
                                ? { 
                                    0: { targetAxisIndex: 0, color: '#4285F4' }, // Actual
                                    1: { targetAxisIndex: 1, color: '#34A853' }, // Cumulative (secondary)
                                    2: { targetAxisIndex: 1, color: '#FB8C00', lineDashStyle: [4, 4] } // Projected Cumulative (secondary)
                                }
                                : chartData[0]?.length === 3
                                ? { 
                                    0: { targetAxisIndex: 0, color: '#4285F4' }, // Value
                                    1: { targetAxisIndex: 1, color: '#34A853' } // Cumulative (secondary)
                                }
                                : undefined
                        }}
                    />
                </div>
            </div>
        );
    }
    function convertHAxisName(name:string) :string{
        switch(name){
            case 'bsw':
                return "BSW (%)"
            case 'gross_barrels':
                return "Gross Barrels"
            case 'gravity':
                return "Corrected Gravity"
        }
            
        return "Unknown"
    }

    function BSWHistogramChart() {
        const [chartData, setChartData] = useState<(string | number)[][]>([['BSW']]);
        const chartOptions = useMemo(() => ({
            title: 'MTD Status',
            legend: { position: 'none' },
            hAxis: { title: 'BSW (%)' },
            vAxis: { title: 'Count' },
            colors: ['#4285F4'],
            histogram: { bucketSize: 1 }, // Adjust bucket size as needed
        }), []);

        useEffect(() => {
            if (!finalTableData?.length) {
                setChartData([['BSW'], [0]]);
                return;
            }
            let data: (string | number)[][];
            switch(dataSeries) {
                case 'bsw':
                    // data = [['BSW'], ...finalTableData.map((item: any) => [Number(item.bsw) || 0])];
                    data = [['BSW'], ...finalTableData.map((item: any) => [Number(item.bsw) || 0])];
                    break;
                case 'gross_barrels':
                    data = [['Gross Barrels'], ...finalTableData.map((item: any) => [Number(item.gross_barrels) || 0])];
                    break;
                case 'gravity':
                    data = [['Gravity'], ...finalTableData.map((item: any) => [Number(item.corrected_gravity) || 0])];
                    break;
                default:
                    data = [['BSW'], ...finalTableData.map((item: any) => [Number(item.bsw) || 0])];
            }   
            // Gather all BSW values (as numbers, filter out null/undefined/NaN)
            setChartData(data);
        }, [finalTableData, dataSeries]);
        const hAxisTitle = convertHAxisName(dataSeries);
        return (
            <div>
                <Chart
                    chartType="Histogram"
                    width="100%"
                    height="50em"
                    data={chartData}
                    options={{...chartOptions,
                        hAxis: { title: hAxisTitle},
                        histogram:{bucketSize:dataSeries==='gross_barrels'?10:1}
                    }}
                />
            </div>
        );
    }

    // Handle producer selection changes
    const handleSelectedProducersChange = (producers: string[]) => {
        setSelectedProducers(producers);
    };
    useEffect(() => {
        if (selectedView != 'monthly') {
            setSelectedChartType('summary_chart');
        }
    }, [selectedView, selectedChartType]);
    useEffect(() => {
        if(selectedMainView !== 'producer') {
            setActiveTab('table');
        }
    }, [selectedMainView]);
    // Handle destinations selection changes
    const handleSelectedDestinationsChange = (destinations: string[]) => {
        setSelectedDestinations(destinations);
    };

    // Handle status selection changes
    const handleStatusChange = (status: string) => {
        setSelectedStatus(status);
    };
    
	// if (isLoading || !apiData?.main_table) {
	// 	return <LoaderWheel />;
	// }
    const TableData = () => {
        return (
        <div className='mtdSummary-mainContainer'>
            <div className='mtdSummary-tableContainer'>
                <MainTable
                    mainTableData={selectedProduct === 'crude_oil' ? finalTableData as MainTableItem[] : undefined}
                    bottomsData={selectedProduct === 'tank_bottoms' ? finalTableData as BottomsMainItem[] : undefined}
                    nglData={selectedProduct === 'ngl' ? finalTableData as NglSchedule[] : undefined}
                    selectedProducers={selectedProducers}
                    selectedProduct={selectedProduct}
                />
            </div>
        </div>
	);
    }
    return(
        <ErrorBoundary fallback={<div>Error loading data</div>}>
            <div className='mtdSummary-topRow'>
                <div className = 'left-col'>
                    <div >
                            <Dropdown
                            options={monthYearOptions}
                            value={selectedMonthYear}
                            onChange={handleMonthYearChange}
                            placeholder="Select Month/Year"
                            sortOrder="desc"
                            height={40}
                        />
                    </div>
                    <div className='mtdSummary-producerSummaryContainer'>
                        <div >
                            <FilterButtons 
                                selectedMainView={selectedMainView}
                                onMainViewChange={setSelectedMainView}
                                selectedView={selectedView}
                                onViewChange={setSelectedView}
                                selectedStatus={selectedStatus}
                                onStatusChange={handleStatusChange}
                                selectedProduct={selectedProduct}
                                onProductViewChange={setSelectedProduct}
                                selectedPersonalFilter={selectedPersonalFilter}
                                onPersonalFilterChange={setSelectedPersonalFilter}
                            />
                        </div>
                    </div>
                    <SummaryList
                        selectedProduct={selectedProduct}
                        mainViewSelected={selectedMainView}
                        mainTableData={finalTableData}
                        summaryData={statusFilteredData}
                        onSelectedProducersChange={handleSelectedProducersChange}
                        onSelectedDestinationsChange={handleSelectedDestinationsChange}
                        selectedMainView={selectedMainView}
                        bottomsData={dateFilteredBottomsData}
                        selectedMonthYear={selectedMonthYear}
                        nglData={dateFilteredNGLData}
                    />
                </div>
                    
                
                <div className='right-col'>
                    <div className="tab-selector">
                        {selectedMainView === 'producer' && (
                            <button className={`tab-selector-button${activeTab === 'chart' ? ' active' : ''}`} onClick={() => setActiveTab('chart')}>
                                Charts
                            </button>
                        )
                        }
                        <button className={`tab-selector-button${activeTab === 'table' ? ' active' : ''}`} onClick={() => setActiveTab('table')}>
                            Table
                        </button>
                    </div>



                    {(() => {
                        switch (activeTab) {
                            case 'chart':
                                return (
                                    <>
                                        {selectedMainView === 'producer' ? (
                                            <>
                                                <div>
                                                    <label>
                                                        Active Chart:&nbsp;
                                                        <select value={selectedChartType} onChange={e => (setSelectedChartType(e.target.value as typeof selectedChartType))}>
                                                            <option value="summary_chart">Summary Chart</option>
                                                            <option value="projection_chart">Projection Chart</option>
                                                            <option value='histogram_chart'>Histogram Chart</option>
                                                        </select>
                                                    </label>
                                                    {selectedChartType !== 'histogram_chart' ? (
                                                        <label>
                                                            Y-Axis:&nbsp;
                                                            <select value={yAxis} onChange={e => setYAxis(e.target.value as typeof yAxis)}>
                                                                <option value="gross_profit">Gross Profit</option>
                                                                <option value="gross_barrels">Gross Barrels</option>
                                                                <option value="net_barrels">Net Barrels</option>
                                                                <option value="margin">Margin (Gross Profit / Gross Barrels)</option>
                                                            </select>
                                                        </label>
                                                    ) : (
                                                        <label>
                                                            Data-Series:&nbsp;
                                                            <select value={dataSeries} onChange={e => setDataSeries(e.target.value as typeof dataSeries)}>
                                                                <option value="bsw">BS&W</option>
                                                                <option value="gross_barrels">Gross Barrels</option>
                                                                <option value="gravity">Gravity</option>
                                                            </select>
                                                        </label>
                                                    )}

                                                </div>
                                                {selectedChartType === 'summary_chart' ? (
                                                    <BarChartData />
                                                ) : selectedChartType === 'projection_chart' ? (
                                                    <LineChartData />
                                                ) : selectedChartType === 'histogram_chart' ? (
                                                    <BSWHistogramChart />
                                                ) : null}
                                            </>
                                        ) : null}
                                    </>

                            );
                            case 'table':
                                return <TableData />;
                            default:
                                return null;
                        }
                    })()}

                </div>
                
            </div>
            
        </ErrorBoundary>    
    );

};
	
export default MainMTDPage;
